import { isTenant } from '@/utils/config'
import UnionIcon from '@xo-union/tk-component-icons'
import cx from 'classnames'
import Link, { LinkProps as NextLinkProps } from 'next/link'
import { HTMLAttributes, PropsWithChildren } from 'react'

import { getUrlToExternalApp } from '@/routing/externalApp'
import stylesTK from './Link.module.scss'
import stylesWW from './ww/LinkWW.module.scss'

export type Size = 'sm' | 'md' | 'lg'

export type Color =
  | 'primary'
  | 'primary-alternative'
  | 'secondary'
  | 'secondaryNoUnderline'
  | 'secondaryNoUnderlineNoHover'
  | 'tertiary'
  | 'tertiaryNoUnderline'
  | 'tertiaryNoUnderlineNoHover'

export type LinkVariantsProps = { size?: Size; color?: Color; disabled?: boolean; isExternal?: boolean }

export type LinkProps = LinkVariantsProps & NextLinkProps & HTMLAttributes<HTMLAnchorElement>

type TextProps = PropsWithChildren<HTMLAttributes<HTMLSpanElement>>

type IconProps = { name?: string; size?: Size } & HTMLAttributes<HTMLDivElement>

const styles = isTenant('ww') ? stylesWW : stylesTK

const DisabledLink = ({ children, color, size }: PropsWithChildren<{ color: string; size: string }>) => {
  const className = cx(styles.wrapper, styles[size], styles[color], styles.disabled)

  return (
    <span data-testid="disabled-link" className={className}>
      {children}
    </span>
  )
}

// It is needed an anchor wrapper for set styles to the component or wrap multiple child elements.
// The legacyBehavior and passHref props are necessary to have a functional component that returns an <a> tag as a child,
// see https://nextjs.org/docs/pages/api-reference/components/link#if-the-child-is-a-custom-component-that-wraps-an-a-tag).
const PaperLink = ({
  size = 'lg',
  color = 'primary',
  disabled = false,
  onClick,
  children,
  className = '',
  isExternal,
  ...props
}: PropsWithChildren<LinkProps>) => {
  if (!children) return null

  if (disabled) {
    return (
      <DisabledLink color={color} size={size}>
        {children}
      </DisabledLink>
    )
  }

  const anchorClassName = cx(styles.wrapper, styles[size], styles[color], className)

  let link

  if (isExternal) {
    let url = String(props.href).trim()

    //check if the url is a relative url and add a slash at the beginning
    if (!url.startsWith('/') && !url.startsWith('http')) {
      url = `/${url}`
    }

    url = getUrlToExternalApp(url)

    link = (
      <a data-testid="external-link" href={url} className={anchorClassName} style={props.style}>
        {children}
      </a>
    )
  } else {
    link = (
      <Link
        {...props}
        href={String(props.href).startsWith('//') ? String(props.href).replace('//', '/') : props.href}
        legacyBehavior
        passHref
      >
        <a className={anchorClassName}>{children}</a>
      </Link>
    )
  }

  return (
    <div data-testid="link" className={styles.linkContainer} onClick={onClick}>
      {link}
    </div>
  )
}

export const Text = ({ children, className, ...props }: TextProps) => {
  if (!children) return null

  return (
    <span className={cx(styles.text, className)} {...props}>
      {children}
    </span>
  )
}

export const Icon = ({ name, size = 'md', className, ...props }: IconProps) => {
  if (!name) return null

  return <UnionIcon name={name} size={size} className={cx(styles.icon, className)} {...props} />
}

PaperLink.Text = Text
PaperLink.Icon = Icon

export default PaperLink
